import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';

interface PackingServiceOption {
  id: string;
  name: string;
  description: string;
  price: number;
}

interface PackingMaterialsOption {
  id: string;
  name: string;
  items: string[];
  rrp: number;
  price: number;
}

interface StorageOption {
  id: string;
  name: string;
  size: string;
  sqft: number;
  priceShortTerm: number; // 1-2 months
  priceLongTerm: number; // 3+ months
  description?: string; // PLACEHOLDER - awaiting ops team content
}

// PLACEHOLDER: Packing service tiers - awaiting final names (Mini/Small/Standard/Large vs Studio/1-Bed/2-Bed) and prices from DR
const packingServiceOptions: PackingServiceOption[] = [
  {
    id: 'mini',
    name: 'Mini Packing Service', // PLACEHOLDER NAME
    description: 'Packing and materials', // PLACEHOLDER
    price: 9900 // £99 PLACEHOLDER
  },
  {
    id: 'small',
    name: 'Small Packing Service', // PLACEHOLDER NAME
    description: 'Packing and materials', // PLACEHOLDER
    price: 18800 // £188 PLACEHOLDER
  },
  {
    id: 'standard',
    name: 'Standard Packing Service', // PLACEHOLDER NAME
    description: 'Packing, materials, and disassembly', // PLACEHOLDER
    price: 23800 // £238 PLACEHOLDER
  },
  {
    id: 'large',
    name: 'Large Packing Service', // PLACEHOLDER NAME
    description: 'Packing, materials, and disassembly', // PLACEHOLDER
    price: 38800 // £388 PLACEHOLDER
  }
];

// Materials-only packs (existing packages for users who decline packing service)
const packingMaterialsOptions: PackingMaterialsOption[] = [
  {
    id: 'small',
    name: 'Small Pack',
    items: [
      '5× Large boxes',
      '5× Extra large boxes',
      '5× Bubble wrap',
      '3× Tape'
    ],
    rrp: 8800,
    price: 8900
  },
  {
    id: 'medium',
    name: 'Medium Pack',
    items: [
      '10× Extra large boxes',
      '5× Large boxes',
      '8× Bubble wrap',
      '5× Tape'
    ],
    rrp: 16050,
    price: 15900
  },
  {
    id: 'large',
    name: 'Large Pack',
    items: [
      '10× Extra large boxes',
      '15× Large boxes',
      '10× Medium boxes',
      '5× Small boxes',
      '10× Bubble wrap',
      '5× Tape'
    ],
    rrp: 26000,
    price: 26900
  }
];

// PLACEHOLDER: Using Lovespace pricing structure - awaiting confirmation from ops team
const storageOptions: StorageOption[] = [
  {
    id: 'mini15',
    name: '15 sq ft Storage',
    size: '15 sq ft',
    sqft: 15,
    priceShortTerm: 7800, // £78/month for 1-2 months PLACEHOLDER
    priceLongTerm: 5900, // £59/month for 3+ months PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  },
  {
    id: 'small25',
    name: '25 sq ft Storage',
    size: '25 sq ft',
    sqft: 25,
    priceShortTerm: 8900, // £89/month PLACEHOLDER
    priceLongTerm: 6700, // £67/month PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  },
  {
    id: 'medium50',
    name: '50 sq ft Storage',
    size: '50 sq ft',
    sqft: 50,
    priceShortTerm: 11200, // £112/month PLACEHOLDER
    priceLongTerm: 8400, // £84/month PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  },
  {
    id: 'large100',
    name: '100 sq ft Storage',
    size: '100 sq ft',
    sqft: 100,
    priceShortTerm: 22400, // £224/month PLACEHOLDER
    priceLongTerm: 16800, // £168/month PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  },
  {
    id: 'xlarge150',
    name: '150 sq ft Storage',
    size: '150 sq ft',
    sqft: 150,
    priceShortTerm: 33700, // £337/month PLACEHOLDER
    priceLongTerm: 25200, // £252/month PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  },
  {
    id: 'xxlarge200',
    name: '200 sq ft Storage',
    size: '200 sq ft',
    sqft: 200,
    priceShortTerm: 45200, // £452/month PLACEHOLDER
    priceLongTerm: 33700, // £337/month PLACEHOLDER
    description: 'PLACEHOLDER - awaiting description from ops team'
  }
];

function RemovalsExtrasComponent(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const gtmEventTracking = useGtmEventTracking();
  const { user } = useContext(UserContext);

  // Get data from location state
  const locationState = location.state as any;
  const selectedTier = locationState?.selectedTier;

  // Packing service state
  const [wantsPackingService, setWantsPackingService] = useState<boolean | null>(null);
  const [selectedPackingService, setSelectedPackingService] = useState<string>('');
  const [wantsPremiumPacking, setWantsPremiumPacking] = useState<boolean | null>(null);

  // Materials-only state (for users who decline packing service)
  const [showMaterialsModal, setShowMaterialsModal] = useState<boolean>(false);
  const [wantsMaterials, setWantsMaterials] = useState<boolean | null>(null);
  const [selectedMaterials, setSelectedMaterials] = useState<string>('');

  // Storage state
  const [wantsStorage, setWantsStorage] = useState<boolean | null>(null);
  const [selectedStorage, setSelectedStorage] = useState<string>('');
  const [storageDuration, setStorageDuration] = useState<'short' | 'long'>('long'); // PLACEHOLDER: default to 3+ months pricing

  useEffect(() => {
    window.scrollTo(0, 0);
    gtmEventTracking({ event: 'booking_step_removals_extras' });
  }, [gtmEventTracking]);

  // Get suggested storage based on tier
  const getSuggestedStorage = React.useCallback(() => {
    if (!selectedTier) return 'medium50';
    const tierMap: { [key: string]: string } = {
      'mini': 'mini15',
      'small': 'small25',
      'standard': 'medium50',
      'large': 'large100'
    };
    return tierMap[selectedTier.id] || 'medium50';
  }, [selectedTier]);

  // Check if selected storage is smaller than recommended
  const getStorageWarning = React.useCallback(() => {
    if (!selectedStorage || !selectedTier) return null;

    const storageOrder = ['mini15', 'small25', 'medium50', 'large100', 'xlarge150', 'xxlarge200'];
    const suggestedStorage = getSuggestedStorage();
    const selectedIndex = storageOrder.indexOf(selectedStorage);
    const suggestedIndex = storageOrder.indexOf(suggestedStorage);

    if (selectedIndex < suggestedIndex) {
      const suggestedOption = storageOptions.find(s => s.id === suggestedStorage);
      return {
        type: 'smaller',
        message: `Based on your ${selectedTier.name.toLowerCase()} move, we recommend ${suggestedOption?.name} (${suggestedOption?.size}) to ensure you have enough space for all your belongings.`
      };
    }

    return null;
  }, [selectedStorage, selectedTier, getSuggestedStorage]);

  // Pre-select packing service based on tier
  useEffect(() => {
    if (selectedTier && wantsPackingService === true && !selectedPackingService) {
      const tierMap: { [key: string]: string } = {
        'mini': 'mini',
        'small': 'small',
        'standard': 'standard',
        'large': 'large'
      };
      const suggestedService = tierMap[selectedTier.id] || 'standard';
      setSelectedPackingService(suggestedService);
    }
  }, [wantsPackingService, selectedTier, selectedPackingService]);

  // Pre-select materials pack based on tier (when user chooses materials-only)
  useEffect(() => {
    if (selectedTier && wantsMaterials === true && !selectedMaterials) {
      const tierMap: { [key: string]: string } = {
        'mini': 'small',
        'small': 'small',
        'standard': 'medium',
        'large': 'large'
      };
      const suggestedMaterials = tierMap[selectedTier.id] || 'medium';
      setSelectedMaterials(suggestedMaterials);
    }
  }, [wantsMaterials, selectedTier, selectedMaterials]);

  // Pre-select storage based on tier
  useEffect(() => {
    if (selectedTier && wantsStorage === true && !selectedStorage) {
      const suggestedStorage = getSuggestedStorage();
      setSelectedStorage(suggestedStorage);
    }
  }, [wantsStorage, selectedTier, selectedStorage, getSuggestedStorage]);

  // Handle packing service "No" selection - show materials modal
  useEffect(() => {
    if (wantsPackingService === false && !showMaterialsModal && wantsMaterials === null) {
      setShowMaterialsModal(true);
    }
  }, [wantsPackingService, showMaterialsModal, wantsMaterials]);

  const isFormValid = () => {
    // Packing validation: must answer service question, and if yes must select tier and premium option
    let packingValid = false;
    if (wantsPackingService === null) {
      packingValid = false; // Must answer packing service question
    } else if (wantsPackingService === false) {
      // If declined service, must answer materials question
      packingValid = wantsMaterials !== null && (wantsMaterials === false || selectedMaterials !== '');
    } else {
      // If wants service, must select tier and answer premium question
      packingValid = selectedPackingService !== '' && wantsPremiumPacking !== null;
    }

    // Storage validation
    const storageValid = wantsStorage === null ? false :
                        wantsStorage === false ? true :
                        selectedStorage !== '';

    return packingValid && storageValid;
  };

  const handleContinue = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (isFormValid()) {
      // Prepare packing data
      let packingData = null;
      if (wantsPackingService && selectedPackingService) {
        const serviceOption = packingServiceOptions.find(p => p.id === selectedPackingService);
        if (serviceOption) {
          packingData = {
            type: 'service',
            service: serviceOption,
            premiumPacking: wantsPremiumPacking,
            // PLACEHOLDER: Premium packing price needs to be added when ops team provides it
          };
        }
      } else if (wantsMaterials && selectedMaterials) {
        const materialsOption = packingMaterialsOptions.find(m => m.id === selectedMaterials);
        if (materialsOption) {
          packingData = {
            type: 'materials',
            materials: materialsOption
          };
        }
      }

      // Prepare storage data
      let storageData = null;
      if (wantsStorage && selectedStorage) {
        const storageOption = storageOptions.find(s => s.id === selectedStorage);
        if (storageOption) {
          const price = storageDuration === 'long' ? storageOption.priceLongTerm : storageOption.priceShortTerm;
          storageData = {
            ...storageOption,
            selectedDuration: storageDuration,
            selectedPrice: price
          };
        }
      }

      // Navigate to collection/delivery address with all accumulated data
      history.push('/removals/collection_address', {
        ...locationState,
        boxes: [], // Removals don't use boxes
        wantsPackingService,
        packingData,
        wantsStorage,
        storageData,
        removalService: true
      });
    }
  };

  if (!selectedTier) {
    history.replace('/removals');
    return <div>Redirecting...</div>;
  }

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>
      <NavigationStatus 
        step={3} 
        locationState={locationState}
      />

      <div className="removals-questions-container">
        <Title>Your move</Title>
        <p className="grey text-center mb-4">
          Add packing materials or storage to your {selectedTier.name.toLowerCase()} removal service.
        </p>

        <div className="form">
          <div id="removals-funnel-wrapper" className="sections questions mt-n3 mt-md-0">
            
            {/* Packing Service Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">Do you require our standard packing service?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    We will provide all the packing materials on the day, professionally pack all your items saving you the time and stress. We find that our packing service also saves on the number of boxes needed to pack.
                  </div>
                  <div className="text-start ls-grey1 mb-3" style={{ fontSize: '14px', fontStyle: 'italic' }}>
                    The standard packing service does not include wrapping furniture in the specialist packing materials. We recommend this service for large furniture items. Please select the premium wrapping service if you would like this.
                  </div>
                  <div className="dropdown-content">
                    <div
                      className={`ls-radio-button ${wantsPackingService === true ? 'selected' : ''}`}
                      onClick={() => setWantsPackingService(true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div
                      className={`ls-radio-button ${wantsPackingService === false ? 'selected' : ''}`}
                      onClick={() => {
                        setWantsPackingService(false);
                        setSelectedPackingService('');
                        setWantsPremiumPacking(null);
                      }}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Packing Service Tiers - Show if user wants packing service */}
            {wantsPackingService === true && (
              <>
                <div className="open transparent dropdown mb-4">
                  <div className="card">
                    <div className="card-header dropdown-header">
                      <div className="header-title">Select your packing service</div>
                    </div>
                    <div className="dropdown-body">
                      <div className="dropdown-content packing-service-options">
                        {packingServiceOptions.map((option) => (
                          <div
                            key={option.id}
                            className={`packing-service-card ${selectedPackingService === option.id ? 'selected' : ''}`}
                            onClick={() => setSelectedPackingService(option.id)}
                          >
                            <div className="service-header">
                              <h4 className="ls-bold">{option.name}</h4>
                              <div className="service-description">{option.description}</div>
                            </div>
                            <div className="service-price ls-bold">£{(option.price / 100).toFixed(2).replace('.00', '')}</div>
                            {selectedPackingService === option.id && (
                              <div className="selected-indicator">✓ Selected</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Premium Packing Question */}
                <div className="open transparent dropdown mb-4">
                  <div className="card">
                    <div className="card-header dropdown-header">
                      <div className="header-title">Do you require premium packing?</div>
                    </div>
                    <div className="dropdown-body">
                      <div className="text-start ls-grey1 mb-3">
                        We recommend you select this if you have large furniture items. Our packers and removal partner uses a specialised wrap to protect your larger items.
                      </div>
                      <div className="dropdown-content">
                        <div
                          className={`ls-radio-button ${wantsPremiumPacking === true ? 'selected' : ''}`}
                          onClick={() => setWantsPremiumPacking(true)}
                        >
                          <div className="marker"></div>
                          <div className="text text-start">Yes</div>
                        </div>
                        <div
                          className={`ls-radio-button ${wantsPremiumPacking === false ? 'selected' : ''}`}
                          onClick={() => setWantsPremiumPacking(false)}
                        >
                          <div className="marker"></div>
                          <div className="text text-start">No</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Materials-Only Modal */}
            {showMaterialsModal && (
              <div className="materials-modal-overlay" onClick={() => setShowMaterialsModal(false)}>
                <div className="materials-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Purchase packing materials?</h3>
                    <button className="modal-close" onClick={() => setShowMaterialsModal(false)}>×</button>
                  </div>
                  <div className="modal-body">
                    <p className="mb-3">
                      It looks like you will be packing your move yourself. Would you like to purchase boxes and packing materials from Keepr?
                    </p>
                    <p className="ls-grey1 mb-4" style={{ fontSize: '14px' }}>
                      These are delivered to your selected collection address. Please notify <a href="mailto:support@kitkeeper.co.uk">support@kitkeeper.co.uk</a> if you wish for the packing materials to be sent to a different address.
                    </p>
                    <div className="modal-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setWantsMaterials(false);
                          setSelectedMaterials('');
                          setShowMaterialsModal(false);
                        }}
                      >
                        No, I don't need materials
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setWantsMaterials(true);
                          setShowMaterialsModal(false);
                        }}
                      >
                        Yes, show me packing materials
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Materials Packs - Show if user wants materials only */}
            {wantsMaterials === true && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Select your packing materials</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="dropdown-content packing-options">
                      {packingMaterialsOptions.map((option) => (
                        <div
                          key={option.id}
                          className={`packing-card ${selectedMaterials === option.id ? 'selected' : ''}`}
                          onClick={() => setSelectedMaterials(option.id)}
                        >
                          <div className="packing-header">
                            <h4 className="ls-bold">{option.name}</h4>
                            <div className="packing-price">
                              <span className="rrp">£{(option.rrp / 100).toFixed(2).replace('.00', '')}</span>
                              <span className="price ls-bold">Pack: £{(option.price / 100).toFixed(2).replace('.00', '')}</span>
                            </div>
                          </div>
                          <div className="packing-items">
                            {option.items.map((item, idx) => (
                              <div key={idx} className="packing-item">• {item}</div>
                            ))}
                          </div>
                          {selectedMaterials === option.id && (
                            <div className="selected-indicator">✓ Selected</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Storage Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">Would you like your belongings stored?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    Need temporary storage during your move? We can help!
                  </div>
                  <div className="dropdown-content">
                    <div 
                      className={`ls-radio-button ${wantsStorage === true ? 'selected' : ''}`}
                      onClick={() => setWantsStorage(true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div 
                      className={`ls-radio-button ${wantsStorage === false ? 'selected' : ''}`}
                      onClick={() => {
                        setWantsStorage(false);
                        setSelectedStorage('');
                      }}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Duration Selector - Show if user wants storage */}
            {wantsStorage === true && (
              <>
                <div className="open transparent dropdown mb-4">
                  <div className="card">
                    <div className="card-header dropdown-header">
                      <div className="header-title">How long do you need storage?</div>
                    </div>
                    <div className="dropdown-body">
                      <div className="text-start ls-grey1 mb-3">
                        Storage duration affects pricing. 3+ months receives discounted rates.
                      </div>
                      <div className="dropdown-content">
                        <div
                          className={`ls-radio-button ${storageDuration === 'short' ? 'selected' : ''}`}
                          onClick={() => setStorageDuration('short')}
                        >
                          <div className="marker"></div>
                          <div className="text text-start">1-2 months</div>
                        </div>
                        <div
                          className={`ls-radio-button ${storageDuration === 'long' ? 'selected' : ''}`}
                          onClick={() => setStorageDuration('long')}
                        >
                          <div className="marker"></div>
                          <div className="text text-start">3+ months (discounted rate)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Storage Size Options */}
                <div className="open transparent dropdown mb-4">
                  <div className="card">
                    <div className="card-header dropdown-header">
                      <div className="header-title">Select your storage size</div>
                    </div>
                    <div className="dropdown-body">
                      {getStorageWarning() && (
                        <div className="alert alert-warning mb-3" style={{
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <span style={{
                            fontSize: '20px',
                            lineHeight: '1',
                            flexShrink: 0
                          }}>⚠️</span>
                          <div style={{ flex: 1 }}>
                            <strong style={{ display: 'block', marginBottom: '4px' }}>Storage size notice:</strong>
                            <span style={{ fontSize: '14px', color: '#856404' }}>
                              {getStorageWarning()?.message}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="dropdown-content storage-options">
                        {storageOptions.map((option) => {
                          const displayPrice = storageDuration === 'long' ? option.priceLongTerm : option.priceShortTerm;
                          return (
                            <div
                              key={option.id}
                              className={`storage-card ${selectedStorage === option.id ? 'selected' : ''}`}
                              onClick={() => setSelectedStorage(option.id)}
                            >
                              <div className="storage-header">
                                <h4 className="ls-bold">{option.name}</h4>
                                <div className="storage-size">{option.size}</div>
                                {option.description && (
                                  <div className="storage-description ls-grey1" style={{ fontSize: '13px', marginTop: '6px' }}>
                                    {option.description}
                                  </div>
                                )}
                              </div>
                              <div className="storage-price-wrapper">
                                <div className="storage-price ls-bold">
                                  £{(displayPrice / 100).toFixed(2).replace('.00', '')}/month
                                </div>
                                {storageDuration === 'long' && option.priceShortTerm > option.priceLongTerm && (
                                  <div className="storage-savings" style={{ fontSize: '12px', color: 'var(--kkUltraViolet)' }}>
                                    Save £{((option.priceShortTerm - option.priceLongTerm) / 100).toFixed(0)}/mo
                                  </div>
                                )}
                              </div>
                              {selectedStorage === option.id && (
                                <div className="selected-indicator">✓ Selected</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div id="funnelFooter" className="mt-3">
            <div className="sticky-bar">
              <div className="sticky-bar-limit"></div>
              <div className="text-center sticky-bar-body">
                <SubmitButton 
                  type="button"
                  disabled={!isFormValid()}
                  onClick={handleContinue}
                >
                  Continue to Addresses
                </SubmitButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RemovalsExtrasComponent;
